import { useEffect, useMemo, useState } from "react";
import { useFirestore } from "react-redux-firebase";
import { FIRESTORE_PURCHASE_ORDER_PAYMENTS_TABLE } from "../../constants/FirestoreConstant";

/**
 * Hook to fetch and manage purchase order payments from Firestore
 * @param {Object} options - Configuration options
 * @param {boolean} options.realtime - Whether to use real-time listener (default: true)
 * @param {string} options.invoiceId - Filter by invoice ID (optional)
 * @param {string} options.supplierId - Filter by supplier ID (optional)
 * @returns {Object} - Payments data and state
 */
export const usePurchaseOrderPayments = (options = {}) => {
  const { realtime = true, invoiceId = null, invoiceIds = null, supplierId = null } = options;
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const firestore = useFirestore();

  // A caller must scope the request via invoiceId, invoiceIds, or supplierId. Without one of
  // those, there is no safe query to run — falling through to an unscoped collection read would
  // return every payment for every supplier and invoice.
  const hasScope = Boolean(invoiceId) || Boolean(supplierId) || Array.isArray(invoiceIds);

  useEffect(() => {
    if (!hasScope) {
      setPayments([]);
      setLoading(false);
      setError(null);
      return undefined;
    }

    setLoading(true);
    let query = firestore.collection(FIRESTORE_PURCHASE_ORDER_PAYMENTS_TABLE);

    // Apply filters
    if (invoiceId) {
      query = query.where("invoiceId", "==", invoiceId);
    }
    if (supplierId) {
      query = query.where("supplierId", "==", supplierId);
    }
    // invoiceIds is handled client-side after fetch (Firestore doesn't support array 'in' with other filters easily)

    if (realtime) {
      const unsubscribe = query.onSnapshot(
        (snapshot) => {
          const data = snapshot.docs
            .map((doc) => ({ id: doc.id, ...doc.data() }))
            .sort((a, b) => (b.paymentDate || 0) - (a.paymentDate || 0));
          setPayments(data);
          setLoading(false);
          setError(null);
        },
        (err) => {
          console.error("Error fetching purchase order payments:", err);
          setError(err);
          setLoading(false);
        },
      );

      return () => unsubscribe();
    } else {
      query
        .get()
        .then((snapshot) => {
          const data = snapshot.docs
            .map((doc) => ({ id: doc.id, ...doc.data() }))
            .sort((a, b) => (b.paymentDate || 0) - (a.paymentDate || 0));
          setPayments(data);
          setLoading(false);
        })
        .catch((err) => {
          console.error("Error fetching purchase order payments:", err);
          setError(err);
          setLoading(false);
        });
    }
  }, [firestore, realtime, invoiceId, supplierId, hasScope]);

  const filteredPayments = useMemo(() => {
    if (!Array.isArray(invoiceIds)) return payments;
    return payments.filter((p) => invoiceIds.includes(p.invoiceId));
  }, [payments, invoiceIds]);

  return { payments: filteredPayments, loading, error };
};
